import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { NgIf } from '@angular/common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import Swal from 'sweetalert2';

import { InscriptionsDTO } from '../../../DTO/InscriptionsDTO';
import { WaitingListInfoDTO } from '../../../DTO/WaitingListInfoDTO';
import { ButtonLoadingComponent } from '../../../components/button-loading/button-loading.component';
import { ApiService } from '../../../services/api.service';
import {CommunityService} from "../../../services/community/community.service";
import {
  CustomFieldPhoneComponent
} from "../../../components/custom-field-input/fields/components/phone/phone.component";
import {NgxIntlTelInputModule} from "ngx-intl-tel-input-gg";

@Component({
  selector: 'app-waiting-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FloatLabelModule,
    InputTextModule,
    DropdownModule,
    TranslateModule,
    NgIf,
    ButtonLoadingComponent,
    CustomFieldPhoneComponent,
    NgxIntlTelInputModule
  ],

  templateUrl: './waiting-list.component.html',
  styleUrl: './waiting-list.component.scss'
})
export class WaitingListComponent {

  // ──────────────── Signals / State ────────────────
  readonly inscription = signal<InscriptionsDTO | null>(null);
  readonly groupId = signal<number | null>(null);
  readonly maxTiquets = signal<number>(1);
  public color: string = "#4e7dee";
  readonly inscripcioForm = signal<FormGroup | null>(null);
  readonly groupsWithWaitingList = computed(() => {
    const inscription = this.inscription();
    if (!inscription?.groups?.length) return [];
    return inscription.groups;
  });
  readonly isFormValid = computed(
      () => this.inscripcioForm()?.valid ?? false
  );


  // ──────────────── Injects ────────────────
  private readonly config = inject(DynamicDialogConfig);
  private readonly apiService = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly communityService = inject(CommunityService);


  public communityName = '';

  // ──────────────── Lifecycle ────────────────
  ngOnInit(): void {
    this.initInscription();
    this.initForm();
    this.initGroupBehaviour();
    this.handlePersonesLogic();
    this.communityService.Get().subscribe(x=>{
      this.color = x.color1;
    })
  }

  // ──────────────── Init helpers ────────────────
  private initInscription(): void {
    this.inscription.set(this.config.data.inscription);
    this.communityName = this.config.data.communityName;
    this.groupId.set(this.config.data.groupId ?? null);

  }

  private initForm(): void {
    const form = this.fb.group({
      nom: ['', Validators.required],
      cognom: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      telefon: ['', Validators.required],
      persones: [1, [Validators.required, Validators.min(1)]],
      grup: [null]
    });

    this.inscripcioForm.set(form);
  }

  private initGroupBehaviour(): void {
    const inscription = this.inscription();
    const form = this.inscripcioForm();
    if (!inscription || !form) return;

    const grupCtrl = form.get('grup')!;
    const hasGroups = (inscription.groups?.length ?? 0) > 0;

    if (hasGroups) {
      grupCtrl.setValidators(Validators.required);
    } else {
      grupCtrl.clearValidators();
    }

    grupCtrl.updateValueAndValidity({ emitEvent: false });

    const forcedGroupId = this.groupId();
    if (!forcedGroupId) return;
    console.log(this.inscription().groups)

    const selectedGroup = inscription.groups?.find(g => g.id === forcedGroupId);
    if (!selectedGroup) return;

    grupCtrl.setValue(selectedGroup);
    grupCtrl.disable({ emitEvent: false });

  }

  // ──────────────── Submit ────────────────
  onSubmit = (): Promise<'success' | 'cancel'> => {
    return new Promise(resolve => {
      const form = this.inscripcioForm();
      if (!form || form.invalid) {
        form?.markAllAsTouched();
        return resolve('cancel');
      }

      const data = this.buildWaitingListInfo(form);

      this.apiService.postInfoWaitingList(this.communityName, data).subscribe({
        next: () => this.handleSuccess(),
        error: err => this.handleError(err)
      });
    });
  };

  private buildWaitingListInfo(form: FormGroup): WaitingListInfoDTO {
    const dto = new WaitingListInfoDTO();
    dto.inscriptionId = this.inscription()!.id;
    dto.name = form.value.nom;
    dto.surname = form.value.cognom;
    dto.email = form.value.email;
    dto.phone = form.value.telefon;
    dto.people = form.value.persones;
    dto.groupId = this.groupId() ?? form.value.grup?.id ?? null;
    return dto;
  }

  // ──────────────── Feedback ────────────────
  private handleSuccess(): void {
    Swal.fire({
      title: this.translate.instant('INSCRIPTIONS.WaitingList.SuccessTitle'),
      text: this.translate.instant('INSCRIPTIONS.WaitingList.SuccessSubtitle'),
      icon: 'success',
      confirmButtonText: this.translate.instant('Accept'),
      customClass: {
        confirmButton: 'swal-confirm-corp'
      },
    }).then(() => this.dialogRef.close());
  }

  private handleError(err: any): void {
    if (err.status === 409 && err.error?.code === 'EMAIL_ALREADY_EXISTS') {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('WAITINGLIST.EmailExistsTitle'),
        text: this.translate.instant('WAITINGLIST.EmailExistsText'),
        confirmButtonText: this.translate.instant('WAITINGLIST.accordButton'),
        customClass: {
          confirmButton: 'swal-confirm-corp'
        },
      }).then(() => this.dialogRef.close());
    }
  }

  // ──────────────── Personas logic ────────────────
  private handlePersonesLogic(): void {
    const form = this.inscripcioForm();
    if (!form) return;

    const personesCtrl = form.get('persones')!;
    this.updatePersonesValidators(personesCtrl);

    // si en el futuro cambia algo dinámico → ya está preparado
  }

  private updatePersonesValidators(ctrl: any): void {
    const inscription = this.inscription();
    if (!inscription) return;

    const max = this.calculateMaxPeople(inscription);

    ctrl.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(max)
    ]);

    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private calculateMaxPeople(inscription: InscriptionsDTO): number {
    const max =
        inscription.multipleInscriptionsAtSameTime === true
            ? inscription.maxMultipleInscriptions ?? 1
            : 1;

    this.maxTiquets.set(max);
    return max;
  }
}
